import { extendTheme, theme as chakraTheme } from '@chakra-ui/react'

// A variante `enclosed` do Chakra, para compor em vez de reescrever: o painel só muda a cor da
// aba ativa, e reimplementar as bordas aqui garantiria divergência na próxima versão da lib.
const chakraEnclosedTabs = chakraTheme.components.Tabs.variants!.enclosed as (props: never) => {
  tab: Record<string, unknown>
}

/**
 * A escala magenta em JavaScript, para os consumidores que **não são Chakra** e por isso não
 * conseguem ler um token: dataset do Chart.js, string de `conic-gradient`, styled-components e o
 * `style` de um `<i>` do LineIcons. Num componente Chakra use `magenta.500`; num arquivo `.css`
 * use `var(--chakra-colors-magenta-500)`, que o próprio Chakra publica em `:root`.
 *
 * O hex estava digitado em 25 arquivos, e em duas grafias: `#fa0069` e `#ff0069` — a segunda é
 * uma deriva, não uma cor a mais.
 */
export const magenta = {
  50: '#ffe6ed',
  100: '#ffb3c4',
  200: '#fb809b',
  300: '#f94d72',
  400: '#f72255',
  500: '#fa0069',
  600: '#d9005a',
  700: '#b2004a',
  800: '#8e003b',
  900: '#6b002c'
} as const

/**
 * A escala de título do painel, por nível. Os valores são os que os três componentes que já
 * emitiam título desenhavam: o `h1` do `PageTitle`/`DetailHero` (o antigo `size="lg"`) e o `h2`
 * do `SectionTitle`.
 */
const HEADING_LEVEL_SIZES: Record<string, { fontSize: string | (string | null)[]; lineHeight: number }> = {
  h1: { fontSize: ['2xl', null, '3xl'], lineHeight: 1.2 },
  h2: { fontSize: 'xl', lineHeight: 1.2 },
  h3: { fontSize: 'md', lineHeight: 1.2 },
  h4: { fontSize: 'sm', lineHeight: 1.2 },
  h5: { fontSize: 'sm', lineHeight: 1.2 },
  h6: { fontSize: 'sm', lineHeight: 1.2 }
}

const theme = extendTheme({
  fonts: {
    heading: 'Montserrat, sans-serif'
  },
  styles: {
    global: {
      '.pac-container': {
        zIndex: '9999 !important'
      },
      'p, ol, ul, dl': {
        marginBottom: 0
      }
    }
  },
  colors: {
    magenta,
    darkGray: {
      50: '#eeeeee',
      100: '#dddddd',
      200: '#cccccc',
      300: '#bbbbbb',
      400: '#aaaaaa',
      500: '#333333',
      600: '#2E2E2E',
      700: '#1F1F1F',
      800: '#171717',
      900: '#1A1A1A'
    },
    abraYellow: {
      50: '#fff9e6',
      100: '#ffedbf',
      200: '#ffe198',
      300: '#ffd571',
      400: '#ffca48',
      500: '#fedc00',
      600: '#d9c400',
      700: '#b2a300',
      800: '#8c8200',
      900: '#665100'
    },
    abraGreen: {
      50: '#F2FCE8',
      100: '#DCF7C0',
      200: '#C5F197',
      300: '#AEEC6F',
      400: '#98E746',
      500: '#67B418',
      600: '#60a816',
      700: '#4D8712',
      800: '#345A0C',
      900: '#1A2D06'
    },
    abraPurple: {
      50: '#F4EBF9',
      100: '#DFC8EF',
      200: '#CAA4E4',
      300: '#B681DA',
      400: '#A15DD0',
      500: '#722fa0',
      600: '#702E9E',
      700: '#542376',
      800: '#38174F',
      900: '#1C0C27'
    },
    abraGray: {
      50: '#888888',
      100: '#777777',
      200: '#666666',
      300: '#555555',
      400: '#444444',
      500: '#333333',
      600: '#2E2E2E',
      700: '#1F1F1F',
      800: '#171717',
      900: '#1A1A1A'
    }
  },
  components: {
    Table: {
      baseStyle: {
        td: {
          verticalAlign: 'middle'
        }
      },
      // Densidade única das tabelas do painel: o `sm` do Chakra (td py 8px / px 16px,
      // fonte 14px; th py 4px, fonte 12px). Não passe `size` nem padding nas páginas —
      // o ritmo das linhas é definido aqui para todas as listagens.
      defaultProps: {
        size: 'sm'
      }
    },
    // Forma única do badge de status. O painel tinha 9 valores de `fontSize` (de `9px` a `sm`,
    // passando por `0.6em`/`0.65em`/`0.7em`/`0.75em`/`0.8em`), `borderRadius` empatado em `md` e
    // `full`, e `px` em quatro valores — para o mesmo elemento, muitas vezes na mesma tela.
    // Não passe `fontSize`, `px` nem `borderRadius` num `<Badge>`.
    Badge: {
      baseStyle: {
        fontSize: 'xs',
        px: 2,
        borderRadius: 'md'
      }
    },
    // Forma única da barra de abas. Os 13 `<Tabs>` do painel são todos `enclosed`, mas dois
    // ficavam azuis por não passar `colorScheme`, e o mesmo
    // `_selected={{ color: 'black', bg: 'gray.100' }}` estava copiado inline em 22 `<Tab>` de 8
    // arquivos (7 só no `CoursesPage`) — e faltava em `FinancialIndicatorsPage`, que por isso
    // era a única tela com a aba ativa pintada de magenta em vez de preto sobre cinza.
    // Não passe `variant`, `colorScheme` nem `_selected` num `<Tabs>`/`<Tab>`.
    Tabs: {
      baseStyle: {
        // O `<Tab>` é um `<button>`, e sem flex o rótulo fica ~3px acima do centro quando a aba
        // carrega um badge ou um ícone ao lado. Era o que o `TAB_STYLE` do `SurveyReportPage`
        // corrigia sozinho. `whiteSpace` fica de fora de propósito: é decisão de cada barra,
        // porque numa TabList sem rolagem o `nowrap` estoura a largura em vez de quebrar.
        tab: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      },
      variants: {
        enclosed: (props: never) => {
          const enclosed = chakraEnclosedTabs(props)

          return {
            ...enclosed,
            tab: {
              ...enclosed.tab,
              _selected: {
                ...(enclosed.tab._selected as Record<string, unknown>),
                color: 'black',
                bg: 'gray.100'
              }
            }
          }
        }
      },
      defaultProps: {
        variant: 'enclosed',
        colorScheme: 'magenta'
      }
    },
    // Uma cor só para liga/desliga. O mesmo "ativo/inativo" era azul numa tela, verde na irmã e
    // magenta na terceira: 53 `Switch` em cinco cores (29 verde, 9 azul, 6 magenta, 5 teal, 1
    // pink), 40 `Checkbox` e 21 `Radio` em duas. Não passe `colorScheme` nem `size` em nenhum
    // dos três — a altura é a do tema, e a única exceção é o `SELECT_CHECKBOX_SIZE` da coluna de
    // seleção de linha, que é densidade de tabela e mora dentro do componente.
    Switch: {
      defaultProps: {
        colorScheme: 'magenta'
      }
    },
    Checkbox: {
      defaultProps: {
        colorScheme: 'magenta'
      }
    },
    Radio: {
      defaultProps: {
        colorScheme: 'magenta'
      }
    },
    // Steps of a multi-step form (`components/FormStepper`): same magenta as the toggles above.
    Stepper: {
      defaultProps: {
        colorScheme: 'magenta'
      }
    },
    // O tooltip do painel é `components/Tooltip`, e **não um `defaultProps` aqui**: o
    // `defaultProps` do Chakra só alimenta o cálculo de estilo (`size`/`variant`/`colorScheme`),
    // e `hasArrow`/`placement`/`openDelay` são comportamento, lidos direto das props do
    // componente. A aparência continua sendo do tema — e `fontSize` não se passa num `<Tooltip>`,
    // que é o que 4 dos 132 faziam.
    // A escala de título é o **nível**, e mora aqui. As páginas passavam `size` em 50 dos 60
    // `<Heading>`, numa escala paralela à que o `PageTitle`, o `SectionTitle` e o `DetailHero`
    // já definem: havia `as="h4" size="lg"` (1,5rem) logo abaixo de um `as="h3" size="sm"`
    // (1rem) — o título mais fundo desenhado maior que o de cima. Não passe `size` num
    // `<Heading>`: o nível é que diz o tamanho.
    //
    // O tamanho sai do `baseStyle` lido a partir do `as`, e **não de um seletor de elemento**
    // (`&:is(h2)`): o seletor ganha em especificidade da classe que o Chakra gera para as props
    // de estilo, então ele atropelaria o `fontSize` que o `PublicFormLayout` e o `SectionTitle`
    // declaram de propósito. Aqui a medida do componente continua vencendo, que é a mesma
    // divisão de "medida é do conteúdo, decoração é do tema" do `FormModal`.
    //
    // `defaultProps.size` aponta para uma entrada vazia porque o `sizes` do Chakra é aplicado
    // **depois** do `baseStyle` e venceria a escala por nível. Os tamanhos da lib continuam lá
    // como escape para quem realmente precisar de um.
    Heading: {
      baseStyle: (props: { as?: string }) => HEADING_LEVEL_SIZES[props.as ?? 'h2'] ?? HEADING_LEVEL_SIZES.h2,
      sizes: {
        level: {}
      },
      defaultProps: {
        size: 'level'
      }
    },
    // As três intenções de texto do painel. Eram 952 `<Text>` em 197 assinaturas de
    // `fontSize`/`color`/`fontWeight`, e só para "legenda / texto auxiliar" havia 10 grafias
    // (`xs`+`gray.500` 81×, `sm`+`gray.500` 54×, `sm`+`gray.600` 37×, `xs`+`gray.600` 29×,
    // `xs`+`gray.400` 27×, mais `12px`, `13px` e `10px`). O par rótulo/valor tinha outras
    // quatro, incompatíveis entre si.
    //
    // `label` e `value` são a grafia que o `<DetailField>` já desenhava — ele passou a ler
    // daqui, então existe uma definição só. Não declare `fontSize`/`color`/`fontWeight` para
    // estas três intenções.
    Text: {
      variants: {
        // Legenda, texto de apoio, a linha pequena embaixo de um valor.
        caption: {
          fontSize: 'xs',
          color: 'gray.500'
        },
        // O rótulo de um par rótulo/valor fora de um `<DetailField>`. Semibold pelo mesmo
        // motivo do `FilterField`: o valor logo abaixo é do mesmo tamanho de texto corrido, e
        // em peso normal o par pesava igual e a ficha virava um bloco cinza só.
        label: {
          fontSize: 'xs',
          fontWeight: 'semibold',
          color: 'gray.500',
          lineHeight: 'tall'
        },
        // O valor desse par.
        value: {
          fontSize: 'sm',
          fontWeight: 'medium',
          color: 'gray.700'
        }
      }
    },
    Button: {
      variants: {
        magenta: {
          bg: 'magenta.500',
          color: 'white',
          _hover: {
            bg: 'magenta.600',
            _disabled: {
              bg: 'magenta.500'
            }
          },
          _active: {
            bg: 'magenta.700'
          },
          _focus: {
            boxShadow: '0 0 0 3px rgba(139,46,139,0.6)'
          }
        }
      }
    }
  }
})

export default theme
